import type ApplicationInstance from '@ember/application/instance';
import { on } from '@ember/modifier';
import { getOwner } from '@ember/owner';
import type Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import { task, type Task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import type IntlService from 'ember-intl/services/intl';
import { modifier } from 'ember-modifier';
import { not, or } from 'ember-truth-helpers';
import WrButton from 'ember-wiser/components/button';
import type DpSessionDeclaredModelView from 'parog-web/models/dp-session-declared-model-view';
import HemodialysisSessionDeclaredModelView from 'parog-web/models/hemodialysis-session-declared-model-view';
import type AnalyticsService from 'parog-web/services/analytics';
import type errors from 'parog-web/services/errors';
import type NavigationService from 'parog-web/services/navigation';

interface Signature {
  Args: {
    sessionMeasurement:
      | HemodialysisSessionDeclaredModelView
      | DpSessionDeclaredModelView;
    currentState: string;
  };
  Blocks: {
    default: [submit: Task<void, []>];
  };
}

export default class DialysisEditionForm extends Component<Signature> {
  @service declare analytics: AnalyticsService;
  @service declare errors: errors;
  @service declare flashMessages: FlashMessagesService;
  @service declare intl: IntlService;
  @service declare navigation: NavigationService;
  @service declare router: RouterService;

  get isDirty(): boolean {
    return this.args.sessionMeasurement.isDirty;
  }

  get isLoading(): boolean {
    return this.submit.isRunning;
  }

  cancel = async () => {
    if (this.isDirty) {
      const result = await this.errors.showPendingChangesetError();

      if (!result.value) {
        return;
      }
    }

    // Refresh the route model in case there is occurredAt update
    const route = (getOwner(this) as ApplicationInstance).lookup(
      `route:${this.navigation.currentRouteName
        .split('.')
        .slice(0, -1)
        .join('.')}`
    ) as Route;

    route?.refresh();

    this.router.transitionTo(
      `${this.navigation.currentRouteName.split('.').slice(0, -1).join('.')}`
    );
  };

  /**
   * Safari iOS does not resize the layout viewport when the keyboard opens:
   * a `fixed` footer stays at the bottom of the page, behind the keyboard.
   * This modifier lifts the footer by the height that the keyboard hides.
   * On Chrome Android, `interactive-widget=resizes-content` (see `index.html`)
   * resizes the layout viewport, so this height is 0.
   */
  stickToViewport = modifier((footer: HTMLElement) => {
    const { visualViewport } = window;

    if (!visualViewport) return;

    const stick = () => {
      const hiddenHeight =
        window.innerHeight - visualViewport.height - visualViewport.offsetTop;

      footer.style.bottom = `${Math.max(0, hiddenHeight)}px`;
    };

    stick();
    visualViewport.addEventListener('resize', stick);
    visualViewport.addEventListener('scroll', stick);

    return () => {
      visualViewport.removeEventListener('resize', stick);
      visualViewport.removeEventListener('scroll', stick);
    };
  });

  submit = task(async () => {
    if (!this.isDirty) return;

    const isValid = await this.args.sessionMeasurement.validate();

    if (!isValid) {
      this.errors.displayError(
        'Veuillez corriger les erreurs avant de sauvegarder'
      );
      return;
    }

    await this.args.sessionMeasurement.save();
    this.#trackPatientMeasurement();

    if (this.args.currentState != 'end') {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }

    if (this.args.currentState == 'end') {
      this.flashMessages.success('Séance de dialyse enregistrée. Merci !', {
        title: 'Dialyse'
      });
      this.router.transitionTo(
        `${this.navigation.currentPatientRouteName}.dashboard`
      );
    } else {
      this.flashMessages.success(
        'Vos mesures ont bien été enregistrées. Merci !'
      );
    }
  });

  #trackPatientMeasurement() {
    const measureType =
      this.args.sessionMeasurement instanceof
      HemodialysisSessionDeclaredModelView
        ? 'hemodialysis-session'
        : 'dp-cycle';

    this.analytics.trackPatientMeasurement(measureType);
  }

  <template>
    {{yield this.submit}}

    <div
      {{this.stickToViewport}}
      class='fixed bottom-0 left-0 right-0 flex gap-2 bg-[#f7f7f7] px-2 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:static sm:flex sm:w-full sm:bg-transparent sm:px-0 sm:py-2'
    >

      <WrButton
        {{on 'click' this.cancel}}
        disabled={{this.isLoading}}
        @variant='outlined'
        class='mr-1 w-full'
        type='button'
      >
        Fermer
      </WrButton>

      <WrButton
        {{on 'click' (perform this.submit)}}
        disabled={{or (not this.isDirty) this.isLoading}}
        class='ml-1 w-full'
        type='submit'
      >
        Sauvegarder
      </WrButton>
    </div>
  </template>
}
